// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {HttpError} from '@app/features/platform/types/EndpointError';
import {failureCode} from '@app/features/platform/utils/ResponseInspection';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import * as ShopierCommands from '@app/features/premium/commands/ShopierCommands';
import styles from '@app/features/premium/components/experience/ShopierCheckoutModal.module.css';
import {SHOPIER_COPY} from '@app/features/premium/utils/ShopierCopy';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import type {ShopierPurchaseResponse} from '@fluxer/schema/src/domains/premium/ShopierSchemas';
import type {MessageDescriptor} from '@lingui/core';
import {useLingui} from '@lingui/react/macro';
import {CheckCircleIcon, CopyIcon, SpinnerGapIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useRef, useState} from 'react';

const POPUP_NAME = 'lafon-shopier-checkout';
const POLL_INTERVAL_MS = 8000;

function describePurchaseError(error: unknown): MessageDescriptor {
	const code = failureCode(error);
	const body = error instanceof HttpError ? error.body : null;
	const reason = body && typeof body === 'object' && 'reason' in body ? body.reason : null;
	if (code === APIErrorCodes.PREMIUM_PURCHASE_BLOCKED) {
		return reason === 'lifetime' ? SHOPIER_COPY.errorLifetime : SHOPIER_COPY.errorPurchaseDisabled;
	}
	if (
		code === APIErrorCodes.PURCHASE_EMAIL_VERIFICATION_REQUIRED ||
		code === APIErrorCodes.EMAIL_VERIFICATION_REQUIRED
	) {
		return SHOPIER_COPY.errorVerifyEmail;
	}
	if (code === APIErrorCodes.UNCLAIMED_ACCOUNT_CANNOT_MAKE_PURCHASES) return SHOPIER_COPY.errorClaimAccount;
	if (code === APIErrorCodes.FEATURE_TEMPORARILY_DISABLED) return SHOPIER_COPY.errorUnavailable;
	if (code === APIErrorCodes.CONFLICT) return SHOPIER_COPY.errorAlreadyOwned;
	return SHOPIER_COPY.createFailed;
}

/** Must be called synchronously from the click handler, otherwise browsers block the popup. */
export function openShopierPopup(url = 'about:blank'): Window | null {
	const width = 520;
	const height = 780;
	const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
	const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
	const popup = window.open(url, POPUP_NAME, `popup=yes,width=${width},height=${height},left=${left},top=${top}`);
	if (popup) {
		try {
			popup.opener = null;
		} catch {}
	}
	return popup;
}

type Phase = 'preparing' | 'ready' | 'waiting' | 'closed' | 'blocked' | 'granted' | 'review' | 'failed';

interface ShopierCheckoutModalProps {
	create: () => Promise<ShopierPurchaseResponse>;
	success?: {title: MessageDescriptor; body: MessageDescriptor};
	popup: Window | null;
	onFinished: () => void;
}

export const ShopierCheckoutModal = observer(({create, success, popup, onFinished}: ShopierCheckoutModalProps) => {
	const {i18n} = useLingui();
	const nameArgs = {premiumProductName: PREMIUM_PRODUCT_NAME};
	const [purchase, setPurchase] = useState<ShopierPurchaseResponse | null>(null);
	const [phase, setPhase] = useState<Phase>('preparing');
	const [copied, setCopied] = useState(false);
	const [failure, setFailure] = useState<MessageDescriptor>(SHOPIER_COPY.createFailed);
	const popupRef = useRef<Window | null>(popup);
	const createRef = useRef(create);
	const finishedRef = useRef(onFinished);
	finishedRef.current = onFinished;

	useEffect(() => () => finishedRef.current(), []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const created = await createRef.current();
				if (cancelled) return;
				setPurchase(created);
				const paymentWindow = popupRef.current;
				if (created.checkout_mode === 'direct' && created.product_url) {
					if (paymentWindow && !paymentWindow.closed) {
						paymentWindow.location.href = created.product_url;
						setPhase('waiting');
					} else {
						setPhase('blocked');
					}
				} else {
					// No single-use checkout could be created: never fall back to the copy-a-code flow.
					paymentWindow?.close();
					popupRef.current = null;
					setFailure(SHOPIER_COPY.errorUnavailable);
					setPhase('failed');
				}
			} catch (error) {
				popupRef.current?.close();
				if (!cancelled) {
					setFailure(describePurchaseError(error));
					setPhase('failed');
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [i18n]);

	const checkStatus = useCallback(async () => {
		if (!purchase) return;
		try {
			const overview = await ShopierCommands.fetchShopierOverview();
			const current = overview.purchases.find((item) => item.reference_code === purchase.reference_code);
			if (current?.status === 'granted') {
				setPhase('granted');
				popupRef.current?.close();
				void PremiumCommands.refreshPremiumState();
			} else if (current?.status === 'review') {
				setPhase('review');
			}
		} catch {}
	}, [purchase]);

	useEffect(() => {
		if (phase !== 'waiting' && phase !== 'closed') return;
		const poll = window.setInterval(() => void checkStatus(), POLL_INTERVAL_MS);
		const watchPopup = window.setInterval(() => {
			if (phase === 'waiting' && popupRef.current?.closed) {
				setPhase('closed');
				void checkStatus();
			}
		}, 1000);
		return () => {
			window.clearInterval(poll);
			window.clearInterval(watchPopup);
		};
	}, [phase, checkStatus]);

	/** Runs inside the click, so the browser treats the popup as user-initiated. */
	const proceedToPayment = () => {
		if (!purchase?.product_url) return;
		void TextCopyCommands.copy(i18n, purchase.reference_code, true).then(setCopied);
		popupRef.current = openShopierPopup(purchase.product_url);
		setPhase(popupRef.current ? 'waiting' : 'blocked');
	};

	return (
		<Modal.Root size="small" centered data-flx="premium.shopier-checkout.modal-root">
			<Modal.Header title={i18n._(SHOPIER_COPY.modalTitle)} data-flx="premium.shopier-checkout.header" />
			<Modal.Content data-flx="premium.shopier-checkout.content">
				<Modal.ContentLayout>
					{phase === 'preparing' && (
						<div className={styles.center}>
							<SpinnerGapIcon className={styles.spinner} size={32} />
							<p className={styles.text}>{i18n._(SHOPIER_COPY.modalPreparing)}</p>
						</div>
					)}
					{phase === 'failed' && (
						<p className={styles.text}>{i18n._(failure, {premiumProductName: PREMIUM_PRODUCT_NAME})}</p>
					)}
					{phase === 'granted' && (
						<div className={styles.center}>
							<CheckCircleIcon className={styles.success} size={48} weight="fill" />
							<h3 className={styles.successTitle}>
								{i18n._(success?.title ?? SHOPIER_COPY.modalSuccessTitle, nameArgs)}
							</h3>
							<p className={styles.text}>{i18n._(success?.body ?? SHOPIER_COPY.modalSuccessBody)}</p>
						</div>
					)}
					{purchase && phase !== 'preparing' && phase !== 'failed' && phase !== 'granted' && (
						<div className={styles.body}>
							{purchase.checkout_mode === 'code' && (
								<>
									<p className={styles.text}>
										{phase === 'ready'
											? i18n._(SHOPIER_COPY.modalReadyHint)
											: copied
												? i18n._(SHOPIER_COPY.modalCodeCopied)
												: i18n._(SHOPIER_COPY.modalCodeNotCopied)}
									</p>
									<div className={styles.codeRow}>
										<code className={styles.code} data-flx="premium.shopier-checkout.code">
											{purchase.reference_code}
										</code>
										<button
											type="button"
											className={styles.copyButton}
											onClick={() => void TextCopyCommands.copy(i18n, purchase.reference_code).then(setCopied)}
											aria-label={i18n._(SHOPIER_COPY.copyCode)}
											data-flx="premium.shopier-checkout.copy"
										>
											{copied ? <CheckCircleIcon size={18} weight="fill" /> : <CopyIcon size={18} weight="bold" />}
											{i18n._(SHOPIER_COPY.copyCode)}
										</button>
									</div>
								</>
							)}
							{phase !== 'ready' && (
								<div className={styles.status} role="status">
									{phase === 'review' ? (
										<span>{i18n._(SHOPIER_COPY.reviewHelp)}</span>
									) : (
										<>
											<SpinnerGapIcon className={styles.spinner} size={18} />
											<span>
												{phase === 'blocked'
													? i18n._(SHOPIER_COPY.modalPopupBlocked)
													: phase === 'closed'
														? i18n._(SHOPIER_COPY.modalPopupClosed)
														: i18n._(SHOPIER_COPY.modalWaiting)}
											</span>
										</>
									)}
								</div>
							)}
						</div>
					)}
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer data-flx="premium.shopier-checkout.footer">
				<Button variant="secondary" onClick={() => ModalCommands.pop()} data-flx="premium.shopier-checkout.close">
					{i18n._(SHOPIER_COPY.modalClose)}
				</Button>
				{purchase?.product_url && (phase === 'ready' || phase === 'closed' || phase === 'blocked') && (
					<Button variant="primary" onClick={proceedToPayment} data-flx="premium.shopier-checkout.proceed">
						{phase === 'ready' ? i18n._(SHOPIER_COPY.proceedToPayment) : i18n._(SHOPIER_COPY.modalOpenShopier)}
					</Button>
				)}
			</Modal.Footer>
		</Modal.Root>
	);
});
